import React, { useState, useEffect, useRef } from "react";
import { FiMessageSquare, FiSend, FiSearch, FiUser } from "react-icons/fi";
import {
  API_ENDPOINTS,
  API_URL,
  WS_ENDPOINTS,
} from "../../../constants/constants";
import { io } from "socket.io-client";
import type { Socket } from "socket.io-client";

interface ChatMessage {
  id: string;
  content: string;
  sender: {
    id: string;
    name: string;
    avatar?: string;
  };
  createdAt: string;
}

interface Contact {
  id: string | number;
  firstName: string;
  lastName: string;
  email: string;
  role?: string;
  fonction?: string;
  service?: string;
  profileImage?: string | null;
  isActive?: boolean;
  lastMessage?: string;
  lastMessageTime?: string;
  unreadCount?: number;
}

// Interface pour les données de message reçues de l'API
interface ApiMessage {
  id: string | number;
  content: string;
  isRead?: boolean;
  attachmentUrl?: string | null;
  createdAt?: string;
  sender_id?: string | number;
  recipient_id?: string | number;
  channel_id?: string | number;
  sender?: {
    id: string | number;
    firstName: string;
    lastName: string;
    email?: string;
    profileImage?: string | null;
  };
  recipient?: {
    id: string | number;
    firstName: string;
    lastName: string;
  };
}

const ProfesseurChat: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [filteredContacts, setFilteredContacts] = useState<Contact[]>([]);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState<{ [key: string]: boolean }>({});
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [page, setPage] = useState(1);
  const [loadedAllMessages, setLoadedAllMessages] = useState(false);

  // Référence au socket pour le conserver entre les rendus
  const socketRef = useRef<Socket | null>(null);

  // Timer pour gérer les événements "is typing"
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Ref pour le div des messages, pour le scroll automatique
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Référence pour la div des messages
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Fonction pour obtenir le nom du contact à partir de son ID
  const getContactName = (senderId: string | number): string => {
    if (!senderId) return "Utilisateur inconnu";

    // Si c'est l'utilisateur actuel, retourner "Moi"
    const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
    if (currentUser?.id && senderId.toString() === currentUser.id.toString()) {
      return "Moi";
    }

    // Sinon, chercher le contact dans la liste des contacts
    const contact = contacts.find(
      (c) => c.id && c.id.toString() === senderId.toString()
    );
    return contact
      ? `${contact.firstName} ${contact.lastName}`
      : "Utilisateur inconnu";
  };

  // Fonction améliorée pour obtenir l'ID utilisateur courant
  const getCurrentUserId = (): string => {
    try {
      // Tentative 1: Récupération standard
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        // Vérification ID directement dans l'objet user
        if (user && user.id) {
          return user.id.toString();
        }
        // Vérification userId dans l'objet user
        if (user && user.userId) {
          return user.userId.toString();
        }
        // Vérification userID dans l'objet user (différence de casse)
        if (user && user.userID) {
          return user.userID.toString();
        }
        // Vérification d'ID dans un format userID: n
        if (user && user.userID) {
          return user.userID.toString();
        }
      }

      // Tentative 2: Récupération du token et décodage si possible
      const token = localStorage.getItem("accessToken");
      if (token) {
        try {
          // Diviser le token en parties (header.payload.signature)
          const parts = token.split(".");
          if (parts.length > 1) {
            // Décoder la partie payload
            const payload = JSON.parse(atob(parts[1]));
            if (
              payload &&
              (payload.id || payload.sub || payload.userId || payload.userID)
            ) {
              return (
                payload.id ||
                payload.sub ||
                payload.userId ||
                payload.userID
              ).toString();
            }
          }
        } catch (e) {
          console.warn("Impossible de décoder le token JWT:", e);
        }
      }

      // Échec de récupération de l'ID
      console.warn("ID utilisateur non trouvé dans localStorage");
      return "";
    } catch (error) {
      console.error(
        "Erreur lors de la récupération de l'ID utilisateur:",
        error
      );
      return "";
    }
  };

  // Initialiser la connexion WebSocket
  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken");

    if (!accessToken) {
      setError("Vous devez être connecté pour accéder au chat");
      setConnected(false);
      return;
    }

    // Nettoyer les erreurs précédentes
    setError(null);

    // Créer le socket avec authentification
    const socket = io(WS_ENDPOINTS.CHAT.MAIN, {
      auth: {
        token: accessToken,
      },
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 20000,
    });

    // Stocker le socket dans la référence
    socketRef.current = socket;

    // Événements de connexion
    socket.on("connect", () => {
      console.log("Connecté au serveur WebSocket");
      setConnected(true);
      setError(null); // Effacer tout message d'erreur précédent

      // Demander la liste des utilisateurs en ligne
      socket.emit("get_online_users", {});
    });

    socket.on("disconnect", () => {
      console.log("Déconnecté du serveur WebSocket");
      setConnected(false);
      // Ne pas montrer d'erreur pour une déconnexion normale
      // Réinitialiser la liste des utilisateurs connectés
      setOnlineUsers(new Set());
    });

    socket.on("connect_error", (err: Error) => {
      console.error("Erreur de connexion au WebSocket:", err);
      setError("Impossible de se connecter au serveur de chat");
      setConnected(false);
    });

    socket.on("error", (err: Error) => {
      console.error("Erreur WebSocket:", err);
      // Ne pas définir d'erreur pour chaque événement d'erreur, seulement en cas de déconnexion
    });

    // Événements de statut des utilisateurs (nouvelle API)
    socket.on(
      "user_status_changed",
      (data: { userId: number; isOnline: boolean; timestamp: Date }) => {
        console.log("Statut utilisateur changé:", data);
        setOnlineUsers((prevOnlineUsers) => {
          const newOnlineUsers = new Set(prevOnlineUsers);
          if (data.isOnline) {
            newOnlineUsers.add(data.userId.toString());
          } else {
            newOnlineUsers.delete(data.userId.toString());
          }
          return newOnlineUsers;
        });
      }
    );

    // Événement initial pour recevoir la liste des utilisateurs en ligne (nouvelle API)
    socket.on("online_users", (data: { userIds: number[] }) => {
      console.log("Liste des utilisateurs en ligne reçue:", data);
      // Convertir les IDs en string pour uniformité
      setOnlineUsers(new Set(data.userIds.map((id) => id.toString())));
    });

    // Événement de nouveau message
    socket.on("new_message", (message: ApiMessage) => {
      console.log("Nouveau message reçu:", message);
      try {
        // Vérifier que le message a un contenu valide
        if (!message || !message.content) {
          console.warn("Message invalide reçu:", message);
          return;
        }

        // Extraire les IDs avec des vérifications nullables
        const senderId =
          message.sender?.id?.toString() || message.sender_id?.toString() || "";
        const recipientId =
          message.recipient?.id?.toString() ||
          message.recipient_id?.toString() ||
          "";

        // Récupérer l'utilisateur courant avec la nouvelle fonction
        const currentUserId = getCurrentUserId();

        // Si c'est un message reçu (pas un message envoyé par l'utilisateur courant)
        if (senderId !== currentUserId) {
          // Incrémenter le compteur de nouveaux messages
          incrementUnreadMessagesCount();

          // Mettre à jour le compteur de messages non lus pour l'expéditeur
          setContacts((prevContacts) =>
            prevContacts.map((contact) => {
              if (contact.id.toString() === senderId) {
                // Mettre à jour les informations du contact avec le dernier message
                return {
                  ...contact,
                  lastMessage: message.content,
                  lastMessageTime: formatDate(
                    message.createdAt || new Date().toISOString()
                  ),
                  unreadCount: (contact.unreadCount || 0) + 1,
                };
              }
              return contact;
            })
          );
        }

        // Si pas de contact sélectionné, ne rien faire pour l'affichage
        if (!selectedContact) {
          return;
        }

        // Vérifier si le message concerne la conversation courante
        const isMessageForCurrentChat =
          (senderId === currentUserId &&
            recipientId === selectedContact.id.toString()) ||
          (senderId === selectedContact.id.toString() &&
            recipientId === currentUserId);

        // Si pas d'ID utilisateur, on est plus permissif mais on log l'avertissement
        if (!currentUserId) {
          console.warn(
            "ID utilisateur non trouvé - traitement des messages temps réel limité"
          );

          // On affiche quand même les messages impliquant le contact sélectionné
          if (
            senderId === selectedContact.id.toString() ||
            recipientId === selectedContact.id.toString()
          ) {
            // Utiliser directement les informations de l'objet sender si disponible
            const senderName = message.sender
              ? `${message.sender.firstName} ${message.sender.lastName}`
              : getContactName(senderId);

            const formattedMessage: ChatMessage = {
              id: message.id.toString(),
              content: message.content,
              sender: {
                id: senderId,
                name: senderName,
              },
              createdAt: message.createdAt || new Date().toISOString(),
            };

            console.log(
              `Message temps réel ajouté (mode permissif): De ${senderId} à ${recipientId}`
            );
            setMessages((prevMessages) => [...prevMessages, formattedMessage]);
          }
          return;
        }

        // Si on a un ID utilisateur, on applique un filtrage strict
        if (isMessageForCurrentChat) {
          // Utiliser directement les informations de l'objet sender si disponible
          const senderName = message.sender
            ? `${message.sender.firstName} ${message.sender.lastName}`
            : getContactName(senderId);

          const formattedMessage: ChatMessage = {
            id: message.id.toString(),
            content: message.content,
            sender: {
              id: senderId,
              name: senderName,
            },
            createdAt: message.createdAt || new Date().toISOString(),
          };

          console.log(
            `Message temps réel ajouté: De ${senderId} à ${recipientId}`
          );
          setMessages((prevMessages) => [...prevMessages, formattedMessage]);

          // Si le message provient du contact sélectionné, marquer comme lu
          if (senderId === selectedContact.id.toString()) {
            markMessagesAsRead();
          }
        } else {
          console.log(
            `Message ignoré: De ${senderId} à ${recipientId} (conversation courante: entre ${currentUserId} et ${selectedContact.id})`
          );
        }
      } catch (error) {
        console.error(
          "Erreur lors du traitement du nouveau message:",
          error,
          message
        );
      }
    });

    // Notification des messages lus
    socket.on("messages_read", (data: { byUserId: number }) => {
      console.log("Messages lus par l'utilisateur:", data.byUserId);

      // Mettre à jour le compteur de messages non lus pour cet utilisateur
      setContacts((prevContacts) =>
        prevContacts.map((contact) => {
          if (contact.id.toString() === data.byUserId.toString()) {
            return {
              ...contact,
              unreadCount: 0,
            };
          }
          return contact;
        })
      );
    });

    // Événement d'utilisateur en train d'écrire (mise à jour pour la nouvelle API)
    socket.on(
      "user_typing",
      (data: { userId: number; channelId?: number; isTyping: boolean }) => {
        console.log("Utilisateur en train d'écrire:", data);
        const userId = data.userId.toString();
        // Mise à jour directe du state avec la bonne clé userId
        setIsTyping((prev) => ({
          ...prev,
          [userId]: data.isTyping,
        }));
      }
    );

    // Nettoyage à la déconnexion
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  // Scroll automatique vers le bas quand de nouveaux messages arrivent
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // Marquer les messages comme lus lorsqu'un contact est sélectionné
  useEffect(() => {
    if (selectedContact && socketRef.current) {
      console.log(
        `Contact sélectionné: ${selectedContact.firstName} ${selectedContact.lastName}`
      );
      // Marquer les messages comme lus
      markMessagesAsRead();

      // Mettre à jour localement le compteur de messages non lus pour ce contact
      setContacts((prevContacts) =>
        prevContacts.map((contact) => {
          if (contact.id === selectedContact.id) {
            return {
              ...contact,
              unreadCount: 0,
            };
          }
          return contact;
        })
      );
    }
  }, [selectedContact]);

  // Fetch contacts from API
  const fetchContacts = async () => {
    setLoading(true);
    try {
      const url = API_ENDPOINTS.AUTH.GET_USERS;
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log("Contacts récupérés:", data); // Ajout d'un log pour déboguer

      if (data && Array.isArray(data)) {
        // Récupérer le dernier message pour chaque contact
        const contactsWithInfo = await Promise.all(
          data.map(async (contact: Contact) => {
            try {
              // Récupérer le dernier message échangé avec ce contact
              const url = API_ENDPOINTS.CHAT.GET_PRIVATE_MESSAGES(
                contact.id.toString()
              );
              const msgResponse = await fetch(url, {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  Authorization: `Bearer ${accessToken}`,
                },
              });

              if (msgResponse.ok) {
                const messages = await msgResponse.json();
                if (
                  messages &&
                  Array.isArray(messages) &&
                  messages.length > 0
                ) {
                  // Récupérer le dernier message
                  const lastMsg = messages[messages.length - 1];

                  // Compter les messages non lus
                  const currentUserId = getCurrentUserId();
                  const unreadMessages = messages.filter(
                    (msg: ApiMessage) =>
                      !msg.isRead &&
                      msg.sender_id?.toString() !== currentUserId &&
                      msg.sender?.id?.toString() !== currentUserId
                  );

                  return {
                    ...contact,
                    profileImage: contact.profileImage
                      ? `${API_URL}/${contact.profileImage}`
                      : null,
                    lastMessage: lastMsg.content,
                    lastMessageTime: formatDate(
                      lastMsg.createdAt || new Date().toISOString()
                    ),
                    unreadCount: unreadMessages.length,
                  };
                }
              }

              // Si pas de messages ou erreur, retourner le contact sans info
              return {
                ...contact,
                profileImage: contact.profileImage
                  ? `${API_URL}/${contact.profileImage}`
                  : null,
                unreadCount: 0,
              };
            } catch (error) {
              console.error(
                `Erreur lors de la récupération des messages pour ${contact.firstName}:`,
                error
              );
              return {
                ...contact,
                profileImage: contact.profileImage
                  ? `${API_URL}/${contact.profileImage}`
                  : null,
                unreadCount: 0,
              };
            }
          })
        );

        setContacts(contactsWithInfo);
        setFilteredContacts(contactsWithInfo);
      } else {
        setContacts([]);
        setFilteredContacts([]);
      }
      setError(null);
    } catch (err) {
      console.error("Erreur lors de la récupération des contacts:", err);
      setError("Une erreur est survenue lors de la récupération des contacts");
      setContacts([]);
      setFilteredContacts([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch messages for selected contact
  const fetchMessages = async (contactId: string) => {
    try {
      const url = API_ENDPOINTS.CHAT.GET_PRIVATE_MESSAGES(contactId);
      const accessToken = localStorage.getItem("accessToken");
      const currentUserId = getCurrentUserId();

      console.log(
        "Tentative de récupération des messages entre utilisateur",
        currentUserId,
        "et contact",
        contactId
      );
      console.log("URL API utilisée:", url);

      if (!accessToken) {
        setError("Token d'authentification manquant");
        return;
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Erreur API:", response.status, errorText);
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`
        );
      }

      const data = await response.json();
      console.log("Messages récupérés (brut):", JSON.stringify(data));

      if (data && Array.isArray(data)) {
        // Transformer les données reçues au format attendu
        const formattedMessages = data.map((msg: ApiMessage) => {
          // Déterminer si l'utilisateur courant est l'expéditeur
          const senderId =
            msg.sender?.id?.toString() || msg.sender_id?.toString() || "";
          const isCurrentUserSender = senderId === currentUserId;

          // Obtenir le nom de l'expéditeur
          const senderName = isCurrentUserSender
            ? "Moi"
            : msg.sender
            ? `${msg.sender.firstName} ${msg.sender.lastName}`
            : getContactName(senderId);

          return {
            id: msg.id.toString(),
            content: msg.content,
            sender: {
              id: senderId,
              name: senderName,
            },
            createdAt: msg.createdAt || new Date().toISOString(),
          };
        });

        console.log(
          `Affichage de ${formattedMessages.length} messages entre utilisateur ${currentUserId} et contact ${contactId}`
        );

        // Définir si on a chargé tous les messages
        setHasMoreMessages(formattedMessages.length >= 20);
        setMessages(formattedMessages);

        // Après avoir chargé les messages, on veut faire défiler en bas
        setTimeout(() => {
          if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
          }
        }, 100);
      } else {
        setMessages([]);
        setHasMoreMessages(false);
        setLoadedAllMessages(true);
      }
    } catch (err) {
      console.error("Failed to fetch messages:", err);
      setMessages([]);
      setError("Impossible de charger les messages");
    }
  };

  useEffect(() => {
    fetchContacts();
  }, []);

  // Filtrer les contacts en fonction de la recherche
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredContacts(contacts);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const filtered = contacts.filter(
        (contact) =>
          `${contact.firstName} ${contact.lastName}`
            .toLowerCase()
            .includes(query) ||
          (contact.email && contact.email.toLowerCase().includes(query))
      );
      setFilteredContacts(filtered);
    }
  }, [searchQuery, contacts]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleContactSelect = (contact: Contact) => {
    setSelectedContact(contact);
    setMessages([]);
    fetchMessages(contact.id.toString());
  };

  // Fonction pour marquer les messages comme lus
  const markMessagesAsRead = () => {
    if (!socketRef.current || !selectedContact || !selectedContact.id) return;

    try {
      console.log(
        `Marquer les messages de l'utilisateur ${selectedContact.id} comme lus`
      );
      // Utiliser le nouvel événement WebSocket
      socketRef.current.emit("read_messages", {
        otherUserId: Number(selectedContact.id),
      });
    } catch (error) {
      console.error("Erreur lors du marquage des messages comme lus:", error);
    }
  };

  // Fonction pour envoyer un message via WebSocket
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();

    if (!newMessage.trim() || !selectedContact || !socketRef.current) {
      return;
    }

    // Récupérer l'ID de l'utilisateur courant
    const currentUserId = getCurrentUserId();
    if (!currentUserId) {
      console.error("ID utilisateur non trouvé pour l'envoi de message");
      return;
    }

    // Créer le payload du message
    const messagePayload = {
      content: newMessage,
      recipientId: Number(selectedContact.id),
    };

    // Générer un ID temporaire pour le message
    const tempId = `temp-${Date.now()}`;

    // Ajouter le message à l'état local immédiatement
    const newChatMessage: ChatMessage = {
      id: tempId,
      content: newMessage,
      sender: {
        id: currentUserId,
        name: "Moi",
      },
      createdAt: new Date().toISOString(),
    };

    setMessages((prevMessages) => [...prevMessages, newChatMessage]);

    // Envoyer via WebSocket
    socketRef.current.emit("send_message", messagePayload);

    // Vider le champ de saisie
    setNewMessage("");

    // Arrêter l'indication de frappe
    sendTypingStatus(false);
  };

  // Fonction pour gérer l'état "en train d'écrire"
  const sendTypingStatus = (isTyping: boolean) => {
    if (!socketRef.current || !selectedContact) return;

    try {
      // Envoyer l'événement de frappe avec le format de la nouvelle API
      socketRef.current.emit("typing", {
        recipientId: Number(selectedContact.id),
        isTyping,
      });
    } catch (error) {
      console.error("Erreur lors de l'envoi du statut de frappe:", error);
    }
  };

  // Gérer la saisie de message avec détection "en train d'écrire"
  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNewMessage(value);

    // Si on commence à écrire, envoyer l'événement
    if (value.length > 0) {
      sendTypingStatus(true);
    } else {
      sendTypingStatus(false);
    }

    // Réinitialiser le timer
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Si on arrête d'écrire pendant 2 secondes, envoyer l'événement "pas en train d'écrire"
    typingTimeoutRef.current = setTimeout(() => {
      sendTypingStatus(false);
    }, 2000);
  };

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Fonction pour réinitialiser les erreurs
  const clearError = () => {
    setError(null);
  };

  // Fonction pour charger plus de messages en scrollant vers le haut
  const loadMoreMessages = async () => {
    if (!selectedContact || isLoadingMoreMessages || !hasMoreMessages) return;

    try {
      setIsLoadingMoreMessages(true);
      // On pourrait adapter cette URL pour prendre en compte la pagination
      // Exemple: `${API_ENDPOINTS.CHAT.GET_PRIVATE_MESSAGES(selectedContact.id.toString())}?page=${page + 1}&limit=20`
      const url = `${API_ENDPOINTS.CHAT.GET_PRIVATE_MESSAGES(
        selectedContact.id.toString()
      )}?page=${page + 1}&limit=20`;
      const accessToken = localStorage.getItem("accessToken");
      const currentUserId = getCurrentUserId();

      console.log(
        `Chargement de messages supplémentaires (page ${
          page + 1
        }) entre utilisateur ${currentUserId} et contact ${selectedContact.id}`
      );

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data && Array.isArray(data) && data.length > 0) {
        // Transformer les données
        const formattedMessages = data.map((msg: ApiMessage) => {
          const senderId =
            msg.sender?.id?.toString() || msg.sender_id?.toString() || "";
          const isCurrentUserSender = senderId === currentUserId;

          const senderName = isCurrentUserSender
            ? "Moi"
            : msg.sender
            ? `${msg.sender.firstName} ${msg.sender.lastName}`
            : getContactName(senderId);

          return {
            id: msg.id.toString(),
            content: msg.content,
            sender: {
              id: senderId,
              name: senderName,
            },
            createdAt: msg.createdAt || new Date().toISOString(),
          };
        });

        // Ajouter les nouveaux messages au début du tableau
        setMessages((prevMessages) => [...formattedMessages, ...prevMessages]);
        setPage(page + 1);
        setHasMoreMessages(data.length >= 20); // Si moins de 20 messages, on a atteint la fin
      } else {
        // Aucun message supplémentaire
        setHasMoreMessages(false);
        setLoadedAllMessages(true);
      }
    } catch (error) {
      console.error(
        "Erreur lors du chargement de messages supplémentaires:",
        error
      );
    } finally {
      setIsLoadingMoreMessages(false);
    }
  };

  // Observer pour détecter quand l'utilisateur atteint le haut de la zone de messages
  useEffect(() => {
    const messagesContainer = messagesContainerRef.current;
    if (!messagesContainer) return;

    // Fonction pour gérer le scroll
    const handleScroll = () => {
      const { scrollTop } = messagesContainer;

      // Si on est près du haut (50px), on charge plus de messages
      if (scrollTop < 50 && !isLoadingMoreMessages && hasMoreMessages) {
        // Sauvegarder la position de défilement et la hauteur actuelle du contenu
        const scrollHeight = messagesContainer.scrollHeight;

        loadMoreMessages().then(() => {
          // Après chargement, maintenir la position relative en ajustant le scroll
          // On attend que le DOM soit mis à jour
          setTimeout(() => {
            if (messagesContainer) {
              const newScrollHeight = messagesContainer.scrollHeight;
              const heightDifference = newScrollHeight - scrollHeight;
              messagesContainer.scrollTop = heightDifference;
            }
          }, 100);
        });
      }
    };

    messagesContainer.addEventListener("scroll", handleScroll);

    return () => {
      messagesContainer.removeEventListener("scroll", handleScroll);
    };
  }, [selectedContact, isLoadingMoreMessages, hasMoreMessages, page]);

  // Réinitialiser les états lorsqu'un nouveau contact est sélectionné
  useEffect(() => {
    if (selectedContact) {
      setPage(1);
      setHasMoreMessages(true);
      setLoadedAllMessages(false);
    }
  }, [selectedContact?.id]);

  // Modifions la fonction renderMessages
  const renderMessages = () => {
    if (messages.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-gray-500 px-4 py-6">
          <div className="bg-gray-100 rounded-full p-4 mb-3">
            <FiMessageSquare size={32} className="text-gray-400" />
          </div>
          <p className="text-center mb-2">
            Aucun message dans cette conversation
          </p>
          <p className="text-center text-sm">
            Envoyez un premier message pour démarrer la conversation avec{" "}
            {selectedContact?.firstName} {selectedContact?.lastName}
          </p>
        </div>
      );
    }

    return (
      <div className="flex flex-col space-y-4 py-4 px-6">
        {/* Indicateur de chargement en haut */}
        {isLoadingMoreMessages && (
          <div className="flex justify-center py-2">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-500"></div>
          </div>
        )}

        {/* Message "fin des messages" quand tous sont chargés */}
        {loadedAllMessages && messages.length > 0 && (
          <div className="text-center text-xs text-gray-500 py-2 border-t border-gray-200">
            Début de la conversation
          </div>
        )}

        {messages.map((message) => {
          // Récupérer l'ID de l'utilisateur courant
          const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
          const isOwnMessage =
            currentUser.id && message.sender.id === currentUser.id.toString();

          if (isOwnMessage) {
            // Message envoyé - à droite
            return (
              <div key={message.id} className="w-full flex justify-end">
                <div className="max-w-[75%] bg-green-600 text-white px-4 py-3 rounded-lg">
                  <p className="break-words">{message.content}</p>
                  <p className="text-xs mt-1 text-green-100 text-right">
                    {formatDate(message.createdAt)}
                  </p>
                </div>
              </div>
            );
          } else {
            // Message reçu - à gauche
            return (
              <div key={message.id} className="w-full flex">
                <div className="flex max-w-[75%]">
                  <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center mr-2 flex-shrink-0">
                    <FiUser size={18} className="text-gray-600" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">
                      {message.sender.name}
                    </p>
                    <div className="bg-white text-gray-800 border border-gray-200 px-4 py-3 rounded-lg">
                      <p className="break-words">{message.content}</p>
                      <p className="text-xs mt-1 text-gray-500 text-right">
                        {formatDate(message.createdAt)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
        })}
        <div ref={messagesEndRef} />
      </div>
    );
  };

  // Fonction pour incrémenter le compteur de nouveaux messages dans le localStorage
  const incrementUnreadMessagesCount = () => {
    try {
      // Récupérer le compteur actuel
      const currentCount = localStorage.getItem("unreadMessagesCount");
      const newCount = currentCount ? Number.parseInt(currentCount) + 1 : 1;

      // Stocker le nouveau compteur
      localStorage.setItem("unreadMessagesCount", newCount.toString());

      // Émettre un événement pour informer d'autres composants (comme le header)
      const event = new CustomEvent("unreadMessagesUpdated", {
        detail: { count: newCount },
      });
      window.dispatchEvent(event);
    } catch (error) {
      console.error(
        "Erreur lors de la mise à jour du compteur de messages non lus:",
        error
      );
    }
  };

  // Fonction pour réinitialiser le compteur de nouveaux messages
  const resetUnreadMessagesCount = () => {
    localStorage.setItem("unreadMessagesCount", "0");
    const event = new CustomEvent("unreadMessagesUpdated", {
      detail: { count: 0 },
    });
    window.dispatchEvent(event);
  };

  // Réinitialiser le compteur lorsqu'on accède au chat
  useEffect(() => {
    resetUnreadMessagesCount();
  }, []);

  return (
    <div className="bg-[#fefaf6] h-full flex flex-col overflow-hidden">
      <div className="container mx-auto py-2 px-4 flex-grow flex flex-col overflow-hidden">
        <div className="flex flex-col w-full h-full overflow-hidden">
          {/* En-tête Chat */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-800 flex items-center">
              <FiMessageSquare className="mr-2 text-green-600" />
              Messagerie
            </h1>
            {!connected ? (
              <span className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded-full flex items-center">
                <span className="w-2 h-2 bg-red-500 rounded-full mr-1.5 animate-pulse"></span>
                Déconnecté du serveur
              </span>
            ) : (
              <span className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded-full flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-1.5"></span>
                Connecté
              </span>
            )}
          </div>

          {/* Afficher le message d'erreur */}
          {error && (
            <div className="py-3 px-4 mb-4 bg-red-100 text-red-700 rounded-md flex justify-between items-center">
              <p>{error}</p>
              <button
                onClick={clearError}
                className="text-red-500 hover:text-red-700 focus:outline-none"
                aria-label="Fermer"
              >
                <span className="text-lg">&times;</span>
              </button>
            </div>
          )}

          {/* Si l'utilisateur n'est pas connecté, afficher un message */}
          {!connected && !error && (
            <div className="py-3 px-4 mb-4 bg-yellow-100 text-yellow-700 rounded-md">
              <p>
                Vous n'êtes pas connecté au serveur de chat. Essai de
                reconnexion en cours...
              </p>
            </div>
          )}

          <div className="flex flex-grow bg-white rounded-lg shadow-md overflow-hidden">
            {/* Liste des contacts */}
            <div className="w-1/3 border-r border-gray-200 flex flex-col overflow-hidden">
              <div className="p-4 border-b border-gray-200">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Rechercher un contact"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="w-full px-4 py-2.5 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  />
                  <div className="absolute left-3 top-2.5 text-gray-400">
                    <FiSearch size={18} />
                  </div>
                </div>
              </div>

              <div className="overflow-y-auto flex-grow">
                {loading ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Chargement des contacts...</p>
                  </div>
                ) : filteredContacts.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-600">Aucun contact trouvé</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {filteredContacts.map((contact) => (
                      <li
                        key={contact.id}
                        className={`px-4 py-3.5 flex items-center cursor-pointer hover:bg-gray-50 transition-colors duration-150 ${
                          selectedContact?.id === contact.id
                            ? "bg-green-50 border-l-4 border-green-500"
                            : ""
                        }`}
                        onClick={() => handleContactSelect(contact)}
                      >
                        <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mr-3 relative flex-shrink-0">
                          {contact.profileImage ? (
                            <img
                              src={contact.profileImage}
                              alt={`${contact.firstName} ${contact.lastName}`}
                              className="w-12 h-12 rounded-full object-cover"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.onerror = null;
                                target.src = "/images/avatar-placeholder.png";
                              }}
                            />
                          ) : (
                            <FiUser size={24} className="text-gray-500" />
                          )}
                          {/* Indicateur de statut en ligne/hors ligne */}
                          <div
                            className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white ${
                              onlineUsers.has(contact.id.toString())
                                ? "bg-green-500"
                                : "bg-gray-400"
                            }`}
                          ></div>
                        </div>
                        <div className="flex-grow min-w-0">
                          <div className="flex justify-between items-baseline">
                            <h3 className="font-semibold text-gray-800 truncate flex items-center">
                              {`${contact.firstName} ${contact.lastName}`}
                              {(contact.unreadCount || 0) > 0 && (
                                <span className="ml-2 bg-green-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                                  {contact.unreadCount}
                                </span>
                              )}
                            </h3>
                            {contact.lastMessageTime && (
                              <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                                {contact.lastMessageTime}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center text-xs text-gray-500 mt-0.5">
                            {contact.fonction && (
                              <span className="truncate">
                                {contact.fonction}
                                {contact.service && ` · ${contact.service}`}
                              </span>
                            )}
                          </div>
                          {contact.lastMessage && (
                            <p className="text-sm text-gray-600 truncate mt-1 pr-4">
                              {contact.lastMessage}
                            </p>
                          )}
                        </div>
                        {/* Badge de messages non lus */}
                        {(contact.unreadCount || 0) > 0 && (
                          <div className="ml-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs flex-shrink-0">
                            {(contact.unreadCount || 0) > 99
                              ? "99+"
                              : contact.unreadCount || 0}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {/* Zone de chat */}
            <div className="w-2/3 flex flex-col overflow-hidden">
              {!selectedContact ? (
                <div className="flex-grow flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <div className="text-4xl mb-4 flex justify-center">
                      <FiMessageSquare />
                    </div>
                    <p>Sélectionnez un contact pour commencer à discuter</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* En-tête de la conversation */}
                  <div className="p-4 border-b border-gray-200 flex items-center bg-white shadow-sm">
                    <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center mr-3 relative">
                      {selectedContact.profileImage ? (
                        <img
                          src={selectedContact.profileImage}
                          alt={`${selectedContact.firstName} ${selectedContact.lastName}`}
                          className="w-12 h-12 rounded-full object-cover"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.onerror = null;
                            target.src = "/images/avatar-placeholder.png";
                          }}
                        />
                      ) : (
                        <FiUser size={24} className="text-gray-500" />
                      )}
                      {/* Indicateur de statut en ligne/hors ligne */}
                      <div
                        className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white ${
                          onlineUsers.has(selectedContact.id.toString())
                            ? "bg-green-500"
                            : "bg-gray-400"
                        }`}
                      ></div>
                    </div>
                    <div className="flex-grow">
                      <h2 className="font-semibold text-lg flex items-center">
                        {`${selectedContact.firstName} ${selectedContact.lastName}`}
                        <span
                          className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                            onlineUsers.has(selectedContact.id.toString())
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-800"
                          }`}
                        >
                          {onlineUsers.has(selectedContact.id.toString())
                            ? "En ligne"
                            : "Hors ligne"}
                        </span>
                      </h2>
                      {selectedContact.fonction && (
                        <p className="text-xs text-gray-500 mt-1">
                          {selectedContact.fonction}{" "}
                          {selectedContact.service &&
                            `· ${selectedContact.service}`}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Messages - Zone défilante */}
                  <div
                    className="flex-grow p-4 overflow-y-auto bg-gray-50"
                    ref={messagesContainerRef}
                  >
                    {renderMessages()}
                    {/* Indicateur "en train d'écrire" */}
                    {isTyping[selectedContact.id.toString()] && (
                      <div className="flex items-center text-xs text-gray-500 italic mb-2 ml-10">
                        <div className="flex space-x-1">
                          <span
                            className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
                            style={{ animationDelay: "0ms" }}
                          ></span>
                          <span
                            className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
                            style={{ animationDelay: "150ms" }}
                          ></span>
                          <span
                            className="w-2 h-2 rounded-full bg-gray-400 animate-bounce"
                            style={{ animationDelay: "300ms" }}
                          ></span>
                        </div>
                        <span className="ml-2">
                          {selectedContact.firstName} est en train d'écrire
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Zone de saisie de message - Fixe en bas */}
                  <form
                    onSubmit={handleSendMessage}
                    className="p-4 border-t border-gray-200 flex items-center bg-white"
                  >
                    <input
                      type="text"
                      value={newMessage}
                      onChange={handleMessageChange}
                      placeholder="Écrivez votre message..."
                      className="flex-grow px-4 py-3 border border-gray-300 rounded-l-md focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
                    />
                    <button
                      type="submit"
                      className={`px-5 py-3 ${
                        connected
                          ? "bg-green-600 hover:bg-green-700"
                          : "bg-gray-400"
                      } text-white rounded-r-md transition-colors duration-200 focus:outline-none flex items-center justify-center`}
                      disabled={!connected}
                    >
                      <FiSend
                        className={
                          connected
                            ? "transform hover:translate-x-1 transition-transform duration-200"
                            : ""
                        }
                      />
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfesseurChat;
